/**
 * Shader Node — Iframe Manager (Host Side)
 *
 * Owns the sandboxed iframe: code injection, input/param/control messages,
 * and hang detection. The iframe has `sandbox="allow-scripts"` (opaque origin,
 * no allow-same-origin), so all communication is by message.
 *
 * Each loaded document gets a session. Its bridge says `genie:hello` with that
 * session on the window, and the manager answers once with a MessagePort; from
 * then on everything travels over the port. The sketch can navigate its own
 * frame, and window messages from the page it lands on look like the sketch's
 * (same source window), but that page never gets the port: it cannot pose as
 * the sketch, and inputs, params and audio frames never reach it.
 */

import { buildSrcdoc } from './genie-runtime';
import type { RuntimeMode, ShaderError, HostInputData, HostMessage, IframeMessage } from './types';
import type { ShaderInputType } from './media/contract';

/** No heartbeat (sent every 1s by the runtime) for this long while visible means the sketch is hung. */
const WATCHDOG_TIMEOUT_MS = 10000;

/** Interval between heartbeat checks */
const WATCHDOG_CHECK_INTERVAL_MS = 2000;

let sessions = 0;

function newSession(): string {
  const random = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : Math.random().toString(36).slice(2);
  return `${++sessions}-${random}`;
}

export interface IframeManagerOptions {
  /** Width of the canvas inside the iframe */
  width?: number;
  /** Height of the canvas inside the iframe */
  height?: number;
  /** Optional CDN overrides for runtime libraries */
  cdn?: string[];
}

export interface IframeManagerCallbacks {
  onOutput?: (handle: string, data: unknown) => void;
  onError?: (error: ShaderError) => void;
  onReady?: () => void;
  /** The runtime library and the user's code have run (after `onReady`). */
  onLoaded?: () => void;
  /** A sketch reported as unresponsive started answering again. */
  onRecover?: () => void;
  onCapture?: (dataUrl: string) => void;
}

export class IframeManager {
  private iframe: HTMLIFrameElement | null = null;
  private container: HTMLElement | null = null;
  private callbacks: IframeManagerCallbacks = {};
  private messageHandler: ((event: MessageEvent) => void) | null = null;
  /** The loaded document's session; '' when nothing is loaded. */
  private session = '';
  /** This session's end of the channel, once its document has said hello. */
  private port: MessagePort | null = null;
  private watchdogTimer: ReturnType<typeof setInterval> | null = null;
  private lastHeartbeat = 0;
  private isAlive = false;
  private hung = false;
  private currentMode: RuntimeMode = 'glsl';
  private currentCode = '';
  private readonly options: Required<Omit<IframeManagerOptions, 'cdn'>> & Pick<IframeManagerOptions, 'cdn'>;

  constructor(options?: IframeManagerOptions) {
    this.options = {
      width: options?.width ?? 500,
      height: options?.height ?? 500,
      cdn: options?.cdn,
    };
  }

  /** Create the sandboxed iframe inside the given container element. */
  create(container: HTMLElement): HTMLIFrameElement {
    this.destroy();
    this.container = container;

    const iframe = document.createElement('iframe');
    iframe.sandbox.add('allow-scripts');
    iframe.className = 'block h-full w-full border-0 bg-black';
    iframe.setAttribute('title', 'Shader preview');
    container.appendChild(iframe);
    this.iframe = iframe;

    this.messageHandler = this.handleHello.bind(this);
    window.addEventListener('message', this.messageHandler);
    return iframe;
  }

  /** Destroy the iframe and clean up all listeners/timers. */
  destroy(): void {
    this.stopWatchdog();
    this.disconnect();
    this.session = '';
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }
    if (this.iframe) {
      this.iframe.srcdoc = '';
      this.iframe.remove();
      this.iframe = null;
    }
    this.isAlive = false;
    this.container = null;
  }

  /** Rebuild the document with the same code and mode. */
  restart(): void {
    if (this.currentCode && this.container) this.loadCode(this.currentCode, this.currentMode);
  }

  /** Load code into the iframe, rebuilding its whole document. */
  loadCode(code: string, mode: RuntimeMode): void {
    if (!this.iframe) return;
    this.currentCode = code;
    this.currentMode = mode;
    this.isAlive = false;
    this.disconnect();
    this.session = newSession();
    this.iframe.srcdoc = buildSrcdoc(mode, code, this.options.width, this.options.height, this.options.cdn, this.session);
    this.startWatchdog();
  }

  /** Stop whatever is running (e.g. the code was cleared). */
  clear(): void {
    this.stopWatchdog();
    this.disconnect();
    this.session = '';
    this.currentCode = '';
    this.isAlive = false;
    if (this.iframe) this.iframe.srcdoc = '';
  }

  /**
   * Send input data to the iframe. `null` clears a disconnected input. Buffers in
   * `transfer` move to the iframe instead of being copied, and are unusable here after.
   * Like every message, it is dropped until the document connects; `onReady` is the
   * moment to send the current state.
   */
  sendInput(handle: ShaderInputType | (string & {}), data: HostInputData, transfer?: Transferable[]): void {
    this.post({ type: 'genie:input', handle, data }, transfer);
  }

  /** Send a parameter value update to the iframe. */
  sendParam(name: string, value: number | string | boolean): void {
    this.post({ type: 'genie:param', name, value });
  }

  /** Pause, resume or capture; restart rebuilds the document. */
  sendControl(action: 'pause' | 'resume' | 'restart' | 'capture'): void {
    if (action === 'restart') {
      this.restart();
      return;
    }
    this.post({ type: 'genie:control', action });
  }

  setCallbacks(callbacks: IframeManagerCallbacks): void {
    this.callbacks = callbacks;
  }

  /** Whether the iframe is currently alive and responding */
  get alive(): boolean {
    return this.isAlive;
  }

  /** The underlying iframe element (if created) */
  get element(): HTMLIFrameElement | null {
    return this.iframe;
  }

  private post(message: HostMessage, transfer?: Transferable[]): void {
    if (!this.port) return;
    if (transfer && transfer.length > 0) this.port.postMessage(message, transfer);
    else this.port.postMessage(message);
  }

  /** The one window message accepted: the current document's hello, answered with its port. */
  private handleHello(event: MessageEvent): void {
    // Only our own iframe; sibling shader nodes post to the same window.
    const target = this.iframe?.contentWindow;
    if (!target || event.source !== target || this.port || !this.session) return;
    const msg = event.data as { type?: unknown; session?: unknown } | null;
    if (!msg || msg.type !== 'genie:hello' || msg.session !== this.session) return;
    const channel = new MessageChannel();
    this.port = channel.port1;
    this.port.onmessage = (e: MessageEvent) => this.handleMessage(e.data as IframeMessage);
    target.postMessage({ type: 'genie:connect', session: this.session }, '*', [channel.port2]);
  }

  private disconnect(): void {
    if (!this.port) return;
    this.port.onmessage = null;
    this.port.close();
    this.port = null;
  }

  private handleMessage(msg: IframeMessage): void {
    if (!msg || typeof msg.type !== 'string') return;

    switch (msg.type) {
      case 'genie:output':
        this.callbacks.onOutput?.(msg.handle, msg.data);
        break;
      case 'genie:error':
        if (typeof msg.message !== 'string') return;
        this.callbacks.onError?.({
          kind: msg.kind,
          message: msg.message,
          line: typeof msg.line === 'number' ? msg.line : undefined,
          column: typeof msg.column === 'number' ? msg.column : undefined,
        });
        break;
      case 'genie:heartbeat':
        this.lastHeartbeat = Date.now();
        if (this.hung) {
          this.hung = false;
          this.callbacks.onRecover?.();
        }
        this.isAlive = true;
        break;
      case 'genie:ready':
        this.isAlive = true;
        this.lastHeartbeat = Date.now();
        this.callbacks.onReady?.();
        break;
      case 'genie:loaded':
        this.callbacks.onLoaded?.();
        break;
      case 'genie:capture':
        // The iframe is untrusted: accept only encoded images.
        if (typeof msg.dataUrl === 'string' && msg.dataUrl.startsWith('data:image/')) this.callbacks.onCapture?.(msg.dataUrl);
        break;
    }
  }

  private startWatchdog(): void {
    this.stopWatchdog();
    this.lastHeartbeat = Date.now();
    this.hung = false;

    this.watchdogTimer = setInterval(() => {
      // Hidden windows throttle timers on both sides; judge only while visible, from a fresh start.
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        this.lastHeartbeat = Date.now();
        return;
      }
      const elapsed = Date.now() - this.lastHeartbeat;
      if (elapsed > WATCHDOG_TIMEOUT_MS && this.isAlive) {
        this.isAlive = false;
        this.hung = true;
        this.callbacks.onError?.({
          kind: 'unresponsive',
          message: `Sketch stopped responding for ${Math.round(elapsed / 1000)}s. Its code may be stuck in a loop. Restart to try again.`,
        });
      }
    }, WATCHDOG_CHECK_INTERVAL_MS);
  }

  private stopWatchdog(): void {
    if (this.watchdogTimer) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }
}
